<template>
  <hs-modal
    :model-value="true"
    size="xl"
    kicker="Client request"
    :title="`Change Request #${request.id}`"
    :description="modalDescription"
    body-class="request-details-modal__body"
    @close="closeModal"
  >
    <div class="request-details">
      <section class="request-details__summary">
        <div class="request-details__summary-copy">
          <hs-icon :name="isStoreDetails ? 'shopping-bag' : 'settings'" tone="primary" soft />
          <div>
            <p>{{ isStoreDetails ? "Store details update" : "RRA EBM settings update" }}</p>
            <span>{{ submittedAtLabel }}</span>
          </div>
        </div>
        <hs-badge :tone="statusTone(request.status)" size="md">
          {{ request.status || "UNKNOWN" }}
        </hs-badge>
      </section>

      <section class="request-details__grid">
        <article class="request-details__card request-details__card--wide">
          <div class="request-details__card-header">
            <div>
              <h3>Requested changes</h3>
              <p>Current value compared with the requested update.</p>
            </div>
            <hs-badge v-if="changedFields.length" tone="primary" size="sm">
              {{ changedFields.length }} changed
            </hs-badge>
          </div>

          <div class="request-details__changes">
            <div
              v-for="item in changeRows"
              :key="item.label"
              class="request-details__change-row"
              :class="{ 'request-details__change-row--changed': item.changed }"
            >
              <div class="request-details__change-label">
                <span>{{ item.label }}</span>
                <hs-badge v-if="item.changed" tone="success" size="sm">Updated</hs-badge>
              </div>
              <div class="request-details__value-pair">
                <div>
                  <span>Current</span>
                  <strong>{{ formatValue(item.oldValue) }}</strong>
                </div>
                <div>
                  <span>Requested</span>
                  <strong>{{ formatValue(item.newValue) }}</strong>
                </div>
              </div>
            </div>
          </div>
        </article>

        <article class="request-details__card">
          <div class="request-details__card-header">
            <div>
              <h3>Status</h3>
              <p>Review state and approval context.</p>
            </div>
          </div>
          <div class="request-details__facts">
            <div class="request-details__fact">
              <span>Status</span>
              <hs-badge :tone="statusTone(request.status)" size="sm">
                {{ request.status || "-" }}
              </hs-badge>
            </div>
            <div v-if="!isStoreDetails" class="request-details__fact">
              <span>EBM activated</span>
              <hs-badge :tone="request.ebm_activated ? 'success' : 'danger'" size="sm">
                {{ request.ebm_activated ? "Yes" : "No" }}
              </hs-badge>
            </div>
            <div v-if="!isStoreDetails" class="request-details__fact">
              <span>VAT activated</span>
              <hs-badge :tone="request.vat_activated ? 'success' : 'danger'" size="sm">
                {{ request.vat_activated ? "Yes" : "No" }}
              </hs-badge>
            </div>
            <div class="request-details__fact">
              <span>Reviewed by</span>
              <strong>{{ request.reviewed_by?.names || "-" }}</strong>
            </div>
            <div class="request-details__fact">
              <span>Review date</span>
              <strong>{{ formatDate(request.review_date) }}</strong>
            </div>
          </div>
        </article>

        <article class="request-details__card">
          <div class="request-details__card-header">
            <div>
              <h3>Submitted by</h3>
              <p>User who requested the change.</p>
            </div>
          </div>
          <div class="request-details__facts">
            <div class="request-details__fact">
              <span>Name</span>
              <strong>{{ request.submitted_by?.names || "-" }}</strong>
            </div>
            <div class="request-details__fact">
              <span>Email</span>
              <strong>{{ request.submitted_by?.email || "-" }}</strong>
            </div>
            <div class="request-details__fact">
              <span>Phone</span>
              <strong>{{ request.submitted_by?.phone_number || "-" }}</strong>
            </div>
            <div class="request-details__fact">
              <span>Submitted at</span>
              <strong>{{ submittedAtLabel }}</strong>
            </div>
          </div>
        </article>

        <article class="request-details__card request-details__card--wide">
          <div class="request-details__card-header">
            <div>
              <h3>Supporting documents</h3>
              <p>Files attached to help review this request.</p>
            </div>
            <hs-badge tone="neutral" size="sm">
              {{ documents.length }} file{{ documents.length === 1 ? "" : "s" }}
            </hs-badge>
          </div>

          <div v-if="documents.length" class="request-details__documents">
            <a
              v-for="doc in documents"
              :key="doc.id || doc.url_path"
              class="request-details__document"
              :href="buildDocumentUrl(doc.url_path)"
              target="_blank"
              rel="noopener noreferrer"
            >
              <hs-icon :name="safeFileIcon(doc.url_path)" tone="primary" soft />
              <div>
                <strong>{{ doc.display_name || documentName(doc.url_path) }}</strong>
                <span v-if="doc.size_in_bytes">{{ bytesToKB(doc.size_in_bytes) }} KB</span>
                <span v-else>Open document</span>
              </div>
              <hs-icon name="external-link" :size="16" />
            </a>
          </div>
          <div v-else class="request-details__empty">
            <hs-icon name="paperclip" tone="neutral" soft />
            <div>
              <strong>No documents attached</strong>
              <span>This request was submitted without supporting files.</span>
            </div>
          </div>
        </article>
      </section>
    </div>

    <template #footer>
      <hs-button variant="secondary" @click="closeModal">
        Close
      </hs-button>
      <hs-button
        v-if="request.status === 'PENDING'"
        variant="danger"
        icon="x-circle"
        :loading="loadingReject"
        @click="rejectRequest"
      >
        Reject
      </hs-button>
      <hs-button
        v-if="request.status === 'PENDING'"
        variant="success"
        icon="check-circle"
        :loading="loadingApprove"
        @click="approveRequest"
      >
        Approve
      </hs-button>
    </template>
  </hs-modal>
</template>

<script>
import { getFileIcon } from "@/helper/utils";
import { HsBadge, HsButton, HsIcon, HsModal } from "@/components/ui";

export default {
  name: "ViewRequestDetails",
  components: {
    HsBadge,
    HsButton,
    HsIcon,
    HsModal,
  },
  props: {
    request: {
      type: Object,
      required: true,
    },
    isStoreDetails: {
      type: Boolean,
      default: false,
    },
    loadingReject: {
      type: Boolean,
      default: false,
    },
    loadingApprove: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["close-modal", "approve-request", "reject-request"],
  computed: {
    modalDescription() {
      return this.isStoreDetails
        ? "Review requested store profile updates before approving or rejecting."
        : "Review requested RRA EBM configuration changes before approving or rejecting.";
    },
    submittedAtLabel() {
      return this.formatDate(this.request.created_at);
    },
    documents() {
      return this.request.documents || [];
    },
    changeRows() {
      if (this.isStoreDetails) {
        return [
          { label: "Store name", oldValue: this.request.current_record?.name, newValue: this.request.name },
          { label: "Location", oldValue: this.request.current_record?.location, newValue: this.request.location },
          { label: "Phone", oldValue: this.request.current_record?.phone_number, newValue: this.request.phone_number },
          { label: "Store type", oldValue: this.request.current_record?.store_type, newValue: this.request.store_type },
        ].map(this.withChangeFlag);
      }

      return [
        { label: "TIN", oldValue: this.request.current_record?.tin, newValue: this.request.tin },
        { label: "Branch ID", oldValue: this.request.current_record?.bhfId, newValue: this.request.bhfId },
        { label: "Device serial no", oldValue: this.request.current_record?.dvcSrlNo, newValue: this.request.dvcSrlNo },
        { label: "MRC", oldValue: this.request.current_record?.mrc, newValue: this.request.mrc },
        { label: "Top message", oldValue: this.request.current_record?.topMsg, newValue: this.request.topMsg },
        { label: "Bottom message", oldValue: this.request.current_record?.btmMsg, newValue: this.request.btmMsg },
      ].map(this.withChangeFlag);
    },
    changedFields() {
      return this.changeRows.filter((row) => row.changed);
    },
  },
  methods: {
    withChangeFlag(item) {
      return {
        ...item,
        changed: this.normalizeValue(item.oldValue) !== this.normalizeValue(item.newValue),
      };
    },
    normalizeValue(value) {
      return value === null || value === undefined ? "" : String(value);
    },
    formatValue(value) {
      if (value === null || value === undefined || value === "") return "-";
      return value;
    },
    formatDate(value) {
      return this.$convertGMTToLocalTime(value) || "-";
    },
    statusTone(status) {
      const map = {
        APPROVED: "success",
        CANCELED: "danger",
        REJECTED: "danger",
        PENDING: "warning",
      };
      return map[status] || "neutral";
    },
    buildDocumentUrl(urlPath) {
      if (!urlPath) return null;
      const base = (process.env.VUE_APP_BACKEND_API || "").replace(/\/$/, "");
      let normalized = urlPath.startsWith("/") ? urlPath : `/${urlPath}`;
      if (base.endsWith("/api") && normalized.startsWith("/api/")) {
        normalized = normalized.substring(4);
      }
      return `${base}${normalized}`;
    },
    safeFileIcon(urlPath) {
      const icon = getFileIcon(urlPath);
      return icon || "file-text";
    },
    documentName(urlPath) {
      if (!urlPath) return "Document";
      return urlPath.split("/").filter(Boolean).pop() || "Document";
    },
    bytesToKB(bytes) {
      return (bytes / 1024).toFixed(2);
    },
    closeModal() {
      this.$emit("close-modal");
    },
    approveRequest() {
      this.$emit("approve-request", this.request.id);
    },
    rejectRequest() {
      this.$emit("reject-request", this.request.id);
    },
  },
};
</script>

<style scoped>
.request-details {
  display: grid;
  gap: 1rem;
}

.request-details__summary {
  align-items: center;
  background: color-mix(in srgb, var(--hs-primary, #3293ec) 6%, var(--hs-surface, #ffffff));
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 20px;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  padding: 1rem;
}

.request-details__summary-copy {
  align-items: center;
  display: flex;
  gap: 0.85rem;
  min-width: 0;
}

.request-details__summary-copy p,
.request-details__card h3,
.request-details__empty strong,
.request-details__document strong {
  color: var(--hs-text, #0f172a);
  font-weight: 950;
  margin: 0;
}

.request-details__summary-copy span,
.request-details__card p,
.request-details__empty span,
.request-details__document span {
  color: var(--hs-muted, #64748b);
  display: flex;
  font-size: 0.82rem;
  font-weight: 750;
  line-height: 1.45;
}

.request-details__grid {
  align-items: start;
  display: grid;
  gap: 1rem;
  grid-template-columns: minmax(0, 1.35fr) minmax(18rem, 0.65fr);
}

.request-details__card {
  background: var(--hs-surface, #ffffff);
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 20px;
  display: grid;
  gap: 1rem;
  padding: 1rem;
}

.request-details__card--wide {
  grid-column: 1;
}

.request-details__card-header {
  align-items: flex-start;
  display: flex;
  gap: 0.85rem;
  justify-content: space-between;
}

.request-details__changes,
.request-details__facts,
.request-details__documents {
  display: grid;
  gap: 0.7rem;
}

.request-details__change-row {
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 16px;
  display: grid;
  gap: 0.75rem;
  padding: 0.85rem;
}

.request-details__change-row--changed {
  background: color-mix(in srgb, #22c55e 7%, var(--hs-surface, #ffffff));
  border-color: color-mix(in srgb, #22c55e 24%, var(--hs-border, rgba(148, 163, 184, 0.22)));
}

.request-details__change-label,
.request-details__fact {
  align-items: center;
  display: flex;
  gap: 0.7rem;
  justify-content: space-between;
}

.request-details__change-label span,
.request-details__fact span {
  color: var(--hs-muted, #64748b);
  font-size: 0.78rem;
  font-weight: 900;
}

.request-details__value-pair {
  display: grid;
  gap: 0.7rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.request-details__value-pair div {
  background: color-mix(in srgb, var(--hs-bg-soft, #f1f5f9) 55%, transparent);
  border-radius: 14px;
  min-width: 0;
  padding: 0.75rem;
}

.request-details__value-pair span {
  color: var(--hs-muted, #64748b);
  display: block;
  font-size: 0.7rem;
  font-weight: 950;
  margin-bottom: 0.3rem;
  text-transform: uppercase;
}

.request-details__value-pair strong,
.request-details__fact strong {
  color: var(--hs-text, #0f172a);
  display: block;
  font-size: 0.86rem;
  font-weight: 900;
  line-height: 1.4;
  overflow-wrap: anywhere;
}

.request-details__fact {
  border-bottom: 1px solid color-mix(in srgb, var(--hs-border, rgba(148, 163, 184, 0.22)) 72%, transparent);
  min-height: 2.4rem;
  padding-bottom: 0.7rem;
}

.request-details__fact:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}

.request-details__document,
.request-details__empty {
  align-items: center;
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 16px;
  display: flex;
  gap: 0.85rem;
  padding: 0.85rem;
}

.request-details__document {
  color: inherit;
  text-decoration: none;
  transition: background 0.18s ease, border-color 0.18s ease;
}

.request-details__document:hover {
  background: color-mix(in srgb, var(--hs-primary, #3293ec) 7%, transparent);
  border-color: color-mix(in srgb, var(--hs-primary, #3293ec) 24%, var(--hs-border, rgba(148, 163, 184, 0.22)));
}

.request-details__document div,
.request-details__empty div {
  flex: 1;
  min-width: 0;
}

.request-details__empty {
  background: color-mix(in srgb, var(--hs-bg-soft, #f1f5f9) 48%, transparent);
}

@media (max-width: 860px) {
  .request-details__summary,
  .request-details__card-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .request-details__grid {
    grid-template-columns: 1fr;
  }

  .request-details__card--wide {
    grid-column: auto;
  }
}

@media (max-width: 560px) {
  .request-details__value-pair {
    grid-template-columns: 1fr;
  }
}
</style>
